import React, { useEffect, useState } from 'react';
import { api } from '../../services/api';
import toast from 'react-hot-toast';
import { DocumentDuplicateIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import VAWorkTimePanel from '../../components/va/VAWorkTimePanel';

const ROTATING_VISIBLE = 3;

function rotatingSlice(items, cursor, count = ROTATING_VISIBLE) {
  if (!items?.length) return [];
  const len = items.length;
  const start = ((cursor % len) + len) % len;
  const slice = [];
  for (let i = 0; i < Math.min(count, len); i += 1) {
    slice.push(items[(start + i) % len]);
  }
  return slice;
}

const VAOverview = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedModel, setExpandedModel] = useState(null);
  const [rotateCursors, setRotateCursors] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/va/overview');
        if (res.data.success) setData(res.data);
      } catch (err) {
        toast.error('Failed to load overview');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const copyText = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const copyAndRotate = (modelId, kind, text, listLength) => {
    copyText(text);
    if (!listLength) return;
    setRotateCursors((prev) => {
      const current = prev[modelId] || { names: 0, bios: 0 };
      return {
        ...prev,
        [modelId]: {
          ...current,
          [kind]: (current[kind] + 1) % listLength
        }
      };
    });
  };

  const getModelCursors = (modelId) => rotateCursors[modelId] || { names: 0, bios: 0 };

  const downloadAsset = async (modelId, type) => {
    try {
      const token = localStorage.getItem('token');
      const base = process.env.REACT_APP_API_URL
        ? String(process.env.REACT_APP_API_URL).replace(/\/+$/, '')
        : (process.env.NODE_ENV === 'production' || window.location.hostname !== 'localhost' ? '/api' : 'http://localhost:3001/api');
      const path = type === 'pfp'
        ? `/va/models/${modelId}/profile-pictures/download`
        : `/va/models/${modelId}/banners/download`;
      const res = await fetch(`${base}${path}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type}-${modelId}.zip`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Download failed');
    }
  };

  if (loading) {
    return <div className="text-slate-500 py-12 text-center">Loading overview...</div>;
  }

  const { config, models } = data || {};

  return (
    <div className="space-y-6">
      <VAWorkTimePanel />

      <div className="grid md:grid-cols-3 gap-4">
        <div className="md:col-span-1 bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
          <div className="text-sm text-slate-500 uppercase tracking-wide">Followers per account</div>
          <div className="mt-2 text-4xl font-bold text-slate-900 dark:text-white">
            {config?.followersPerAccount ?? '—'}
          </div>
        </div>
        <div className="md:col-span-2 bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Who to follow</h2>
          <p className="mt-3 text-sm text-slate-600 dark:text-slate-300 whitespace-pre-wrap">
            {config?.filterRules?.description || 'No filter rules configured yet.'}
          </p>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 p-6 shadow-sm">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Western names (European / English / USA)</h2>
        <p className="mt-3 text-sm text-slate-600 dark:text-slate-300 whitespace-pre-wrap">
          {config?.filterRules?.westernNames || 'No guidance configured yet.'}
        </p>
      </div>

      <div className="space-y-4">
        <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Bio update assets (per model)</h2>
        <p className="text-sm text-slate-500">
          Use these PFPs, banners, bios, and display names when setting up accounts in GoLogin.
          Three display names and three bios are shown at a time — copy advances through the full list.
        </p>
        {(models || []).length === 0 && (
          <p className="text-sm text-slate-500">No models assigned to your account. Contact admin.</p>
        )}
        {(models || []).map((model) => {
          const cfg = model.bioUpdateConfig || {};
          const names = cfg.names || [];
          const bios = cfg.bios || [];
          const cursors = getModelCursors(model._id);
          const visibleNames = rotatingSlice(names, cursors.names);
          const visibleBios = rotatingSlice(bios, cursors.bios);
          const open = expandedModel === model._id;
          return (
            <div key={model._id} className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm">
              <button
                type="button"
                onClick={() => setExpandedModel(open ? null : model._id)}
                className="w-full px-6 py-4 flex justify-between items-center text-left hover:bg-slate-50 dark:hover:bg-slate-800/50"
              >
                <span className="font-semibold text-slate-900 dark:text-white">{model.name}</span>
                <span className="text-sm text-slate-400">{open ? 'Hide' : 'Show assets'}</span>
              </button>
              {open && (
                <div className="px-6 pb-6 space-y-6 border-t border-slate-100 dark:border-slate-800 pt-4">
                  <div className="flex flex-wrap gap-3">
                    <button
                      type="button"
                      onClick={() => downloadAsset(model._id, 'pfp')}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 text-sm font-medium"
                    >
                      <ArrowDownTrayIcon className="w-4 h-4" /> Download PFPs (ZIP)
                    </button>
                    <button
                      type="button"
                      onClick={() => downloadAsset(model._id, 'banner')}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 text-sm font-medium"
                    >
                      <ArrowDownTrayIcon className="w-4 h-4" /> Download banners (ZIP)
                    </button>
                  </div>
                  <p className="text-xs text-slate-400 italic">
                    Click the copy icon on any display name or bio to copy it. The list auto-rotates to the next items after each copy.
                  </p>
                  <div>
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300">Display names</h4>
                      {names.length > 0 && (
                        <span className="text-xs text-slate-400">
                          Showing {visibleNames.length} of {names.length}
                        </span>
                      )}
                    </div>
                    {names.length === 0 ? (
                      <p className="text-sm text-slate-500">No display names configured.</p>
                    ) : (
                      <div className="grid sm:grid-cols-3 gap-2">
                        {visibleNames.map((n, idx) => (
                          <button
                            key={`${model._id}-name-${cursors.names}-${idx}-${n._id || n.text}`}
                            type="button"
                            onClick={() => copyAndRotate(model._id, 'names', n.text, names.length)}
                            className="inline-flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 text-sm text-left hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                          >
                            <span className="truncate">{n.text}</span>
                            <DocumentDuplicateIcon className="w-4 h-4 shrink-0 opacity-50" />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <h4 className="text-sm font-medium text-slate-700 dark:text-slate-300">Bios</h4>
                      {bios.length > 0 && (
                        <span className="text-xs text-slate-400">
                          Showing {visibleBios.length} of {bios.length}
                        </span>
                      )}
                    </div>
                    {bios.length === 0 ? (
                      <p className="text-sm text-slate-500">No bios configured.</p>
                    ) : (
                      <div className="grid sm:grid-cols-3 gap-2">
                        {visibleBios.map((b, idx) => (
                          <div
                            key={`${model._id}-bio-${cursors.bios}-${idx}-${b._id || b.text}`}
                            className="flex flex-col gap-2 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 text-sm min-h-[5.5rem]"
                          >
                            <span className="flex-1 text-slate-700 dark:text-slate-200 line-clamp-4">{b.text}</span>
                            <button
                              type="button"
                              onClick={() => copyAndRotate(model._id, 'bios', b.text, bios.length)}
                              className="self-end text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
                              aria-label="Copy bio"
                            >
                              <DocumentDuplicateIcon className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {(cfg.profilePictures || []).length > 0 && (
                    <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                      {cfg.profilePictures.map((p) => (
                        <img key={p._id || p.url} src={p.displayUrl || p.url} alt="" className="rounded-lg aspect-square object-cover border border-slate-200 dark:border-slate-700" />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default VAOverview;
